import { Menu, rem } from "@mantine/core";
import { LogoIllumi, LogoSubstack } from "@src/assets/icons";
import config from "@src/config";
import { toIllumiDeepLink } from "@src/utils/app-url";

import IconNotebookTabs from "@src/assets/icons/lucide/notebook-tabs.svg?react";
import IconBug from "@src/assets/icons/tabler/bug.svg?react";
import IconDiscord from "@src/assets/icons/tabler/discord.svg?react";
import IconDownload from "@src/assets/icons/tabler/download.svg?react";
import IconLinkedin from "@src/assets/icons/tabler/linkedin.svg?react";
import IconMail from "@src/assets/icons/tabler/mail.svg?react";
import IconPackage from "@src/assets/icons/tabler/package.svg?react";
import IconYoutube from "@src/assets/icons/tabler/youtube.svg?react";
import { NativeLikeTooltip as Tooltip } from "@src/components/ui/native-like-tooltip";

interface NavBarMenuProps {
	target: React.ReactNode;
	tooltip?: string;
}

export const NavBarMenu = ({ target, tooltip }: NavBarMenuProps) => {
	const isElectron = Boolean(window.electronAPI?.isElectron);
	const desktopDownloadUrl = config.desktopDownloadUrl;
	const menuTarget = <Menu.Target>{target}</Menu.Target>;
	return (
		<Menu
			shadow="xl"
			radius="md"
			width={rem(240)}
			position="top-start"
			withinPortal
			// Above agent-chat drawer (1800); below focus-mode / splash.
			zIndex={2000}
		>
			{tooltip ? (
				<Tooltip label={tooltip} position="top">
					{menuTarget}
				</Tooltip>
			) : (
				menuTarget
			)}
			<Menu.Dropdown>
				<Menu.Item
					className="hover:bg-surface-hover-subtle"
					leftSection={
						<LogoIllumi style={{ width: rem(20), height: rem(20) }} />
					}
					component="a"
					href={config.officialWebsiteUrl}
					target="_blank"
					fz="md"
				>
					Official Website
				</Menu.Item>
				{!isElectron ? (
					<Menu.Item
						className="hover:bg-surface-hover-subtle"
						leftSection={
							<LogoIllumi style={{ width: rem(20), height: rem(20) }} />
						}
						component="a"
						href={toIllumiDeepLink(
							typeof window !== "undefined"
								? `${window.location.pathname}${window.location.search}${window.location.hash}`
								: "",
						)}
						fz="md"
					>
						Open in Desktop App
					</Menu.Item>
				) : null}
				{!isElectron && desktopDownloadUrl ? (
					<Menu.Item
						className="hover:bg-surface-hover-subtle"
						leftSection={<IconDownload width={20} height={20} />}
						component="a"
						href={desktopDownloadUrl}
						target="_blank"
						fz="md"
					>
						Download App
					</Menu.Item>
				) : null}
				<Menu.Item
					className="hover:bg-surface-hover-subtle"
					leftSection={<IconDiscord width={20} height={20} />}
					component="a"
					href={config.discordUrl}
					target="_blank"
					fz="md"
				>
					Join Community
				</Menu.Item>
				<Menu.Item
					className="hover:bg-surface-hover-subtle"
					leftSection={
						<LogoSubstack
							className="text-text-primary"
							style={{ width: rem(20), height: rem(20) }}
						/>
					}
					component="a"
					href={config.newsletterUrl}
					target="_blank"
					fz="md"
				>
					Newsletter
				</Menu.Item>
				<Menu.Item
					className="hover:bg-surface-hover-subtle"
					leftSection={<IconYoutube width={20} height={20} />}
					component="a"
					href={config.youtubeUrl}
					target="_blank"
					fz="md"
				>
					Youtube
				</Menu.Item>
				<Menu.Item
					className="hover:bg-surface-hover-subtle"
					leftSection={<IconLinkedin width={20} height={20} />}
					component="a"
					href={config.linkedinUrl}
					target="_blank"
					fz="md"
				>
					LinkedIn
				</Menu.Item>
				<Menu.Item
					className="hover:bg-surface-hover-subtle"
					leftSection={<IconMail width={20} height={20} />}
					component="a"
					href={config.contactUsUrl}
					target="_blank"
					fz="md"
				>
					Contact Us
				</Menu.Item>
				<Menu.Item
					className="hover:bg-surface-hover-subtle"
					leftSection={<IconPackage width={20} height={20} />}
					component="a"
					href={config.productUpdateUrl}
					target="_blank"
					fz="md"
				>
					Product Update
				</Menu.Item>
				<Menu.Item
					className="hover:bg-surface-hover-subtle"
					leftSection={<IconNotebookTabs width={20} height={20} />}
					component="a"
					href={config.userGuideUrl}
					target="_blank"
					fz="md"
				>
					User Guide
				</Menu.Item>
				<Menu.Item
					className="hover:bg-surface-hover-subtle"
					leftSection={<IconBug width={20} height={20} />}
					component="a"
					href={config.bugReportFormUrl}
					target="_blank"
					fz="md"
				>
					Report a bug
				</Menu.Item>
				<Menu.Divider />
				<Menu.Label>Version v.{config.appVersion}</Menu.Label>
			</Menu.Dropdown>
		</Menu>
	);
};
